import { nil, CircularTemplatesApiEnvironments, EventsApiEnvironments } from "@types";
import { ObjectMap } from "@types";

/**
 * Creates a Promise that resolves after a setTimeout delay.
 * @param milliseconds - Number of milliseconds to wait before the
 *      promise is resolved.
 * @returns A promise that is asynchronously resolved after the specified delay.
 */
export function wait(milliseconds: number = 0): Promise<void> {
    return new Promise((resolve) => {
        window.setTimeout(() => {
            resolve();
        }, milliseconds);
    });
}

/**
 * Convenient utility for calling a function that may or may not be undefined/null.
 * Does nothing if the provided function is undefined/null.
 * @param func - The function to be called.
 * @returns the return value of the executed function, or undefined if the function is null/undefined.
 */
export function safeInvoke<TArgs extends any[], TReturn>(
    func: ((...args: TArgs) => TReturn) | nil,
    ...args: TArgs
): TReturn | undefined {
    if (func) {
        return func(...args);
    }

    return undefined;
}

/**
 * Converts a currency value from cents to dollars.
 * @param centsValue - The money value in cents.
 * @returns the dollar value based on the cents value input.
 */
export function convertCurrencyFromCents(centsValue: number): number {
    return centsValue / 100;
}

/**
 * Take a money amount as a pure number and
 * converts it to a formatted currency string.
 * @param amount - The pure number money value.
 * @returns the formatted currency value.
 */
export function formatToCurrency(amount: number): string {
    const separator = ",";
    const negativeSign = amount < 0 ? "-" : "";
    const formattedAmount = Math.abs(Math.floor(amount * 100) / 100).toFixed(2);
    let separatorCount;

    if (amount.toString().indexOf(".") !== -1) {
        const tempAmount = amount.toString().substr(0, amount.toString().indexOf("."));
        separatorCount = tempAmount.length > 3 ? formattedAmount.length % 3 : 0;
    } else {
        separatorCount = amount.toString().length > 3 ? formattedAmount.length % 3 : 0;
    }

    return `${negativeSign}$${
        (separatorCount ? formattedAmount.substr(0, separatorCount) + separator : "") +
        formattedAmount.substr(separatorCount).replace(/(\\d{3})(?=\\d)/g, "$1" + separator)
    }`;
}

/**
 * Extracts the Environment URL needed for Circular API calls.
 * @param appEnv - The app environment.
 * @returns the URL for the API based on the app environment.
 */
export function extractCircularApiEnvironmentUrl(appEnv: string): string {
    let envUrl = CircularTemplatesApiEnvironments.PRODUCTION;

    if (appEnv === "prod_mirror") {
        envUrl = CircularTemplatesApiEnvironments.PRODUCTION_MIRROR;
    } else if (appEnv === "stg") {
        envUrl = CircularTemplatesApiEnvironments.STAGING;
    } else if (appEnv === "dev") {
        envUrl = CircularTemplatesApiEnvironments.DEVELOPMENT;
    }

    return envUrl;
}

/**
 * Extracts the Environment URL needed for Circular Event API calls.
 * @param appEnv - The app environment.
 * @returns the URL for the API based on the app environment.
 */
export function extractEventsApiEnvironmentUrl(appEnv: string): string {
    let envUrl = EventsApiEnvironments.PRODUCTION;

    if (appEnv === "prod_mirror") {
        envUrl = EventsApiEnvironments.PRODUCTION_MIRROR;
    } else if (appEnv === "stg") {
        envUrl = EventsApiEnvironments.STAGING;
    } else if (appEnv === "dev") {
        envUrl = EventsApiEnvironments.DEVELOPMENT;
    }

    return envUrl;
}

/**
 * Extracts all query parameters and values
 * within the provided URL "search" string.
 * @returns an object containing all query string key/value entries.
 */
export function extractUrlQueryParams(): ObjectMap<string> {
    const keyValueStringList: string[] = window.location.search.substr(1).split("&");
    const queryParamsObj: ObjectMap<string> = {};

    for (const keyValueString of keyValueStringList) {
        const keyValueList = keyValueString.split("=", 2);

        if (keyValueList.length === 1) {
            queryParamsObj[keyValueList[0]] = "";
        } else {
            queryParamsObj[keyValueList[0]] = decodeURIComponent(keyValueList[1].replace(/\+/g, " "));
        }
    }

    return queryParamsObj;
}
